import { useMutation } from '@apollo/client'
import { Button, Form, Input, Select, Typography, Upload } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import useTranslation from 'app/lib/hooks/useTranslation'
import { notify } from 'app/lib/Notify'
import { CREATE_CUSTOMER_INCIDENT_MUTATION } from 'app/queries/Incidents'
import testIds from 'test_ids.json'

import SubjectSelect, { subjectValue } from './SubjectSelect'
import { IncidentSubject, IncidentSubjectType } from './typing'

const INCIDENT_TYPE_GROUPS = {
  delivery: [
    'late_delivery',
    'failed_delivery',
    'wrong_machine',
    'incomplete_delivery',
    'machine_quality'
  ],
  during_rental: ['breakdown', 'slow_repair', 'theft_or_vandalism', 'environmental_damage'],
  pickup_and_billing: ['late_pickup', 'invoice_dispute'],
  other: ['other']
}

const ids = testIds.modules.incident

type Values = {
  incidentType: string
  subject: string
  description: string
  files?: UploadFile[]
}

type Payload = {
  createCustomerIncident: {
    incident?: { id: string; reference: string }
    errors: { path: string[]; message: string }[]
  }
}

type Props = {
  subject?: IncidentSubject
  onSuccess?: () => void
}

const ReportForm: React.FC<Props> = ({ subject, onSuccess }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<Values>()
  const [createIncident, { loading }] = useMutation<Payload>(CREATE_CUSTOMER_INCIDENT_MUTATION, {
    context: { useUploadLink: true }
  })
  const tIncident = (id: string, values?: Record<string, string>) =>
    t({ id: `record.incident.${id}` }, values)
  const required = [{ required: true }]

  const notifyFailure = (messages: string[]) =>
    notify({
      intent: 'danger',
      message: tIncident('report.failure'),
      additionalOptions: { description: messages.join(' ') }
    })

  const onFinish = async ({ incidentType, subject: selected, description, files = [] }: Values) => {
    const [subjectType, subjectId] = selected.split(':') as [IncidentSubjectType, string]
    let response: Awaited<ReturnType<typeof createIncident>>
    try {
      response = await createIncident({
        variables: {
          input: {
            incidentType,
            description,
            subjectType,
            subjectId,
            files: files.map((file) => file.originFileObj)
          }
        }
      })
    } catch (error) {
      notifyFailure([(error as Error).message])
      return
    }

    const result = response.data?.createCustomerIncident
    const messages = [
      ...(result?.errors || []).map((error) => error.message),
      ...(response.errors || []).map((error) => error.message)
    ]

    if (result?.incident && !messages.length) {
      notify({
        intent: 'success',
        message: tIncident('report.success', { reference: result.incident.reference })
      })
      onSuccess?.()
    } else {
      notifyFailure(messages)
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ subject: subject && subjectValue(subject) }}>
      <Form.Item name="incidentType" label={tIncident('attributes.incident_type')} rules={required}>
        <Select
          id={ids.incident_type}
          virtual={false}
          options={Object.entries(INCIDENT_TYPE_GROUPS).map(([group, types]) => ({
            label: tIncident(`attributes.incident_type_groups.${group}`),
            options: types.map((type) => ({
              value: type,
              label: tIncident(`attributes.incident_type_options.${type}`)
            }))
          }))}
          optionRender={(option) => (
            <div style={{ whiteSpace: 'normal' }}>
              <div>{option.label}</div>
              <Typography.Text type="secondary">
                {tIncident(`attributes.incident_type_definitions.${option.value}`)}
              </Typography.Text>
            </div>
          )}
        />
      </Form.Item>
      <Form.Item name="subject" label={tIncident('attributes.subject')} rules={required}>
        <SubjectSelect id={ids.subject} initialSubject={subject} />
      </Form.Item>
      <Form.Item name="description" label={tIncident('attributes.description')} rules={required}>
        <Input.TextArea
          id={ids.description}
          rows={5}
          placeholder={tIncident('attributes.description_placeholder')}
        />
      </Form.Item>
      <Form.Item
        name="files"
        label={tIncident('attributes.files')}
        valuePropName="fileList"
        getValueFromEvent={(event) => event?.fileList}>
        <Upload
          beforeUpload={() => false}
          multiple
          accept="image/jpeg,image/png,image/heic,image/webp,image/gif,application/pdf"
          listType="picture">
          <Button>{tIncident('attributes.files_button')}</Button>
        </Upload>
      </Form.Item>
      <div className="d-flex justify-content-end">
        <Button type="primary" htmlType="submit" loading={loading} data-testid={ids.submit}>
          {tIncident('report.submit')}
        </Button>
      </div>
    </Form>
  )
}

export default ReportForm
